import { Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BillboardListCard } from '@/features/billboards/components/list/BillboardListCard';
import type { BillboardListItem } from '@/features/billboards/utils/billboardsListUtils';

type WishlistGridProps = {
  isPending: boolean;
  items: BillboardListItem[];
};

export function WishlistGrid({ isPending, items }: WishlistGridProps) {
  const navigate = useNavigate();

  if (isPending) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }, (_, index) => (
          <div
            key={index}
            className="overflow-hidden rounded-2xl border border-[#E8ECF0] bg-white"
          >
            <div className="aspect-[16/10] animate-pulse bg-[#EEF1F4]" />
            <div className="space-y-3 p-4">
              <div className="h-4 w-3/4 animate-pulse rounded bg-[#EEF1F4]" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-[#EEF1F4]" />
              <div className="h-12 animate-pulse rounded-xl bg-[#F3F5F7]" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-[var(--dash-line)] bg-white px-6 py-16 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]">
          <Heart className="h-6 w-6" />
        </div>
        <p className="m-0 text-lg font-semibold text-[var(--dash-ink)]">
          Your wishlist is empty
        </p>
        <p className="mt-2 mb-5 max-w-sm text-sm text-[var(--dash-muted)]">
          Browse screens and tap the heart to save them here for later.
        </p>
        <button
          type="button"
          onClick={() => navigate('/screens')}
          className="dash-btn-gradient inline-flex h-11 items-center justify-center rounded-xl px-5 text-sm font-semibold text-white"
        >
          Browse screens
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {items.map((item, index) => (
        <BillboardListCard
          key={item._id || item.id || index}
          item={{ ...item, isWishlisted: item.isWishlisted ?? true }}
          priority={index < 4}
          detailBasePath="/screens"
        />
      ))}
    </div>
  );
}
